import { Icon } from "../Icon.jsx";
import { RouteBadge } from "../RouteBadge.jsx";

const PHASE_RANK = {
  PREPARATION: 0,
  LOADING: 1,
  READY_TO_DEPART: 2,
  IN_PROGRESS: 3,
  RETURNING: 4,
  POST_TRIP_CHECK: 5,
  CLOSED: 6,
};

function upper(value) {
  return String(value || "").trim().toUpperCase();
}

function phaseRank(value) {
  return PHASE_RANK[upper(value)] ?? 0;
}

function isLegacyLog(value) {
  return upper(value) === "LOG";
}

function displayOperationalValue(value, fallback) {
  const normalized = String(value || "").trim();
  return normalized && !isLegacyLog(normalized) ? normalized : fallback;
}

function displayCoDriverValue(value) {
  const normalized = String(value || "").trim();
  if (isLegacyLog(normalized)) return "Placeholder LOG — perlu diperbaiki";
  return normalized || "Tanpa Co-driver";
}

export function buildOperationalCheckpoints({
  contract,
  execution,
  fleetOrder,
  preparations,
  summary,
}) {
  const totalSo = Number(summary?.totalSo) || 0;
  const doCreated = Number(summary?.doCreated) || 0;
  const executionRank = phaseRank(execution?.phase);
  const stockStatuses = preparations.map((item) => upper(item.stockStatus));
  const detailReady =
    totalSo > 0 &&
    preparations.length === totalSo &&
    preparations.every(
      (item) => item.doStatus === "DO_CREATED" || Number(item.itemCount) > 0,
    );
  const stockChecked =
    totalSo > 0 &&
    preparations.length === totalSo &&
    stockStatuses.every(
      (status) => status && !["BELUM_DIHITUNG", "BELUM_DICEK"].includes(status),
    );
  const stockIssueCount = stockStatuses.filter((status) =>
    ["KURANG", "PERLU_MAPPING_UOM", "PERLU_DATA_SKU", "PERLU_CEK"].includes(
      status,
    ),
  ).length;
  const outstandingSources = (contract?.sources || []).filter(
    (source) =>
      source.sourceType === "SALES_ORDER" &&
      source.readinessStatus !== "ALREADY_CREATED",
  );
  const reviewableStockCount = outstandingSources.filter(
    (source) =>
      source.writeCapability?.itemEditorReady &&
      (source.blockers || []).some(
        (blocker) => blocker.code === "STOCK_NOT_SAFE",
      ),
  ).length;
  const hardIssueCount = outstandingSources.filter(
    (source) => !source.writeCapability?.itemEditorReady,
  ).length;
  const fleetCompleted =
    upper(fleetOrder?.status) === "COMPLETED" ||
    executionRank >= PHASE_RANK.CLOSED;

  return [
    {
      id: "PLANNING",
      label: "Perencanaan FO",
      status: "Selesai",
      state: "complete",
      detail: "Fleet Order sudah tersedia.",
    },
    {
      id: "DETAIL",
      label: "Detail SO",
      status: totalSo === 0 ? "Bermasalah" : detailReady ? "Selesai" : "Belum lengkap",
      state: totalSo === 0 ? "danger" : detailReady ? "complete" : "pending",
      detail:
        totalSo === 0
          ? "Tidak ada Sales Order aktif."
          : detailReady
            ? `${totalSo} Sales Order memiliki detail.`
            : "Sinkronkan detail Sales Order.",
    },
    {
      id: "STOCK",
      label: "Pemeriksaan Stok",
      status: !stockChecked
        ? "Belum selesai"
        : hardIssueCount > 0
          ? "Perlu tindak lanjut"
          : reviewableStockCount > 0
            ? "Siap direview"
          : "Selesai",
      state:
        !stockChecked
          ? "pending"
          : hardIssueCount > 0 || reviewableStockCount > 0
            ? "attention"
            : "complete",
      detail: !stockChecked
        ? "Stok belum diperiksa lengkap."
        : hardIssueCount > 0
          ? `${hardIssueCount} Sales Order memiliki data yang belum siap.`
          : reviewableStockCount > 0
            ? `${reviewableStockCount} Sales Order memiliki Qty yang perlu direview.`
            : stockIssueCount > 0
              ? `${stockIssueCount} Sales Order memiliki catatan stok.`
              : "Seluruh stok siap direview.",
    },
    {
      id: "DO",
      label: "Pembuatan DO",
      status:
        totalSo > 0 && doCreated === totalSo
          ? "Selesai"
          : doCreated > 0
            ? "Sebagian"
            : "Belum selesai",
      state:
        totalSo > 0 && doCreated === totalSo
          ? "complete"
          : doCreated > 0
            ? "attention"
            : "pending",
      detail: `${doCreated}/${totalSo} Delivery Order tersedia.`,
    },
    {
      id: "LOADING",
      label: "Siap Loading",
      status:
        executionRank >= PHASE_RANK.READY_TO_DEPART
          ? "Selesai"
          : executionRank >= PHASE_RANK.LOADING
            ? "Berlangsung"
            : "Belum dimulai",
      state:
        executionRank >= PHASE_RANK.READY_TO_DEPART
          ? "complete"
          : executionRank >= PHASE_RANK.LOADING
            ? "current"
            : "pending",
      detail:
        executionRank >= PHASE_RANK.READY_TO_DEPART
          ? "Loading telah dikonfirmasi."
          : executionRank >= PHASE_RANK.LOADING
            ? "Proses loading berlangsung."
            : "Menunggu proses operasional.",
    },
    {
      id: "TRIP",
      label: "Perjalanan",
      status:
        executionRank >= PHASE_RANK.POST_TRIP_CHECK
          ? "Selesai"
          : executionRank >= PHASE_RANK.IN_PROGRESS
            ? "Sedang berjalan"
            : "Belum dimulai",
      state:
        executionRank >= PHASE_RANK.POST_TRIP_CHECK
          ? "complete"
          : executionRank >= PHASE_RANK.IN_PROGRESS
            ? "current"
            : "pending",
      detail: execution?.phaseLabel || "Menunggu keberangkatan.",
    },
    {
      id: "COMPLETED",
      label: "Selesai",
      status: fleetCompleted ? "Selesai" : "Belum selesai",
      state: fleetCompleted ? "complete" : "pending",
      detail: fleetCompleted
        ? "Fleet Order telah ditutup."
        : "Menunggu penyelesaian perjalanan.",
    },
  ];
}

export function getOperationalException({ execution, summary }) {
  const totalSo = Number(summary?.totalSo) || 0;
  const doCreated = Number(summary?.doCreated) || 0;
  const tripStarted = phaseRank(execution?.phase) >= PHASE_RANK.IN_PROGRESS;

  if (tripStarted && totalSo > 0 && doCreated < totalSo) {
    return {
      title: "Perjalanan dimulai sebelum seluruh Delivery Order tersedia.",
      detail: `${totalSo - doCreated} Delivery Order masih perlu diselesaikan atau direkonsiliasi.`,
    };
  }

  return null;
}

export function DoDetailToolbar({ fleetOrder, onBack }) {
  return (
    <div className="do-detail-toolbar">
      <div className="do-detail-breadcrumb" aria-label="Breadcrumb">
        <button onClick={onBack} type="button">
          <Icon name="chevronLeft" size={15} />
          Pembuatan DO
        </button>
        <span aria-hidden="true">/</span>
        <strong>{fleetOrder.foNumber}</strong>
      </div>

      <div className="do-detail-toolbar__actions">
        <a
          className="secondary-button do-detail-view-fo"
          href={`/fleet-orders/${encodeURIComponent(fleetOrder.id)}?tab=summary`}
        >
          Lihat Fleet Order
        </a>
      </div>
    </div>
  );
}

export function DoDetailCommandCard({
  depotWarehouse,
  execution,
  fleetOrder,
  formatDate,
  formatTimestamp,
  formatVolume,
  formatWeight,
  statusBadge,
  summary,
}) {
  const contextItems = [
    {
      icon: "location",
      label: "Depot",
      value:
        depotWarehouse?.depotName ||
        fleetOrder.depot?.depotName ||
        "Belum dipilih",
      detail:
        depotWarehouse?.depotCode || fleetOrder.depot?.depotCode || "Kode belum tersedia",
    },
    {
      icon: "clock",
      label: "Jadwal",
      value: formatDate(fleetOrder.departureDate),
      detail: `Pulang ${formatDate(fleetOrder.estimatedReturnDate)}`,
    },
    {
      icon: "truck",
      label: "Armada",
      value: displayOperationalValue(
        fleetOrder.vehiclePlate,
        "Kendaraan belum ditentukan",
      ),
      detail: displayOperationalValue(
        fleetOrder.vehicleType,
        "Jenis kendaraan belum tersedia",
      ),
    },
    {
      icon: "user",
      label: "Tim",
      value: displayOperationalValue(
        fleetOrder.driverName,
        "Driver belum ditentukan",
      ),
      detail: `Co-driver: ${displayCoDriverValue(
        fleetOrder.coDriverName,
      )}`,
    },
    {
      icon: "route",
      label: "Rute FO",
      value: fleetOrder.foRouteName || "Belum ditentukan",
      detail: `${summary.totalSo} Sales Order`,
    },
    {
      icon: "document",
      label: "Status Dokumen",
      value: `${summary.doCreated}/${summary.totalSo} DO`,
      detail:
        Number(summary.totalSo) > 0 && Number(summary.doCreated) === Number(summary.totalSo)
          ? "Seluruh DO tersedia"
          : `${Math.max(0, Number(summary.totalSo) - Number(summary.doCreated))} DO belum tersedia`,
    },
    {
      icon: "database",
      label: "Muatan",
      value: formatWeight(fleetOrder.totalWeightKg),
      detail: formatVolume(fleetOrder.totalVolumeM3),
    },
  ];

  return (
    <section className="do-detail-command-card">
      <header>
        <div>
          <span className="do-detail-command-card__eyebrow">Workspace Pembuatan DO</span>
          <div className="do-detail-command-card__title-row">
            <h1>{fleetOrder.foNumber}</h1>
            {statusBadge}
            <RouteBadge name={fleetOrder.foRouteName} />
          </div>
          <p>
            {summary.totalSo} Sales Order
            <span aria-hidden="true"> · </span>
            Berangkat {formatDate(fleetOrder.departureDate)}
          </p>
        </div>
        <div className="do-detail-command-card__updated">
          <span>Data Fleet Order diperbarui</span>
          <strong>{formatTimestamp(fleetOrder.updatedAt)}</strong>
        </div>
      </header>

      <div className="do-detail-command-card__context">
        {contextItems.map((item) => (
          <div key={item.label}>
            <span className="do-detail-command-card__context-icon" aria-hidden="true">
              <Icon name={item.icon} size={17} />
            </span>
            <span>
              <small>{item.label}</small>
              <strong>{item.value}</strong>
              <em>{item.detail}</em>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function DoOperationalCheckpoints({
  contract,
  execution,
  fleetOrder,
  preparations,
  summary,
}) {
  const checkpoints = buildOperationalCheckpoints({
    contract,
    execution,
    fleetOrder,
    preparations,
    summary,
  });

  return (
    <section className="do-operational-checkpoints">
      <header>
        <div>
          <span>Informasi Fleet Order</span>
          <strong>Ringkasan tahap operasional</strong>
        </div>
        <small>Status berdasarkan aktivitas terbaru</small>
      </header>

      <div className="do-operational-checkpoints__grid">
        {checkpoints.map((checkpoint, index) => (
          <article className={`is-${checkpoint.state}`} key={checkpoint.id}>
            <span className="do-operational-checkpoints__number">
              {checkpoint.state === "complete" ? (
                <Icon name="check" size={14} />
              ) : (
                index + 1
              )}
            </span>
            <div>
              <strong>{checkpoint.label}</strong>
              <span>{checkpoint.status}</span>
              <small>{checkpoint.detail}</small>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function DoOperationalException({ execution, onReview, summary }) {
  const exception = getOperationalException({ execution, summary });

  if (!exception) return null;

  return (
    <section className="do-operational-exception" role="alert">
      <span className="do-operational-exception__icon" aria-hidden="true">
        <Icon name="warning" size={18} />
      </span>
      <div>
        <strong>Dokumen belum lengkap saat perjalanan berjalan</strong>
        <span>{exception.detail}</span>
      </div>
      <button className="secondary-button" onClick={onReview} type="button">
        Lihat Sales Order
      </button>
    </section>
  );
}
